import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

export interface ILoginFormInputState {
  email: string;
  emailError: string;
  password: string;
  passwordError: string;
  showPassword: boolean;
  resetCode: string;
  step: number;
  error: string;
  isNextLoading: boolean;
  isSubmitLoading: boolean;
  failedLogin: number;
  selectedValue: string;
  dialog: boolean;
  magicLink: boolean;
  loading: boolean;
  noDefault: boolean;
  isSupported: boolean;
  sdkReady: boolean;
  emailList: any[];
  twoFACode: string;
  twoFAError: string;
  isTwoFALoading: boolean;
  showTwoFA: boolean;
}

export const initialState: ILoginFormInputState = {
  email: "",
  emailError: "",
  password: "",
  passwordError: "",
  showPassword: false,
  resetCode: "",
  step: 1,
  error: "",
  isNextLoading: false,
  isSubmitLoading: false,
  failedLogin: 0,
  selectedValue: "1",
  dialog: false,
  magicLink: false,
  loading: false,
  noDefault: true,
  isSupported: false,
  sdkReady: false,
  emailList: [],
  twoFACode: "",
  twoFAError: "",
  isTwoFALoading: false,
  showTwoFA: false,
};

const LoginFormInputSlice = createSlice({
  name: "loginFormInput",
  initialState: initialState,
  reducers: {
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setEmailError: (state, action: PayloadAction<string>) => {
      state.emailError = action.payload;
    },
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    setPasswordError: (state, action: PayloadAction<string>) => {
      state.passwordError = action.payload;
    },
    setShowPassword: (state, action: PayloadAction<boolean>) => {
      state.showPassword = action.payload;
    },
    setResetCode: (state, action: PayloadAction<string>) => {
      state.resetCode = action.payload;
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setIsNextLoading: (state, action: PayloadAction<boolean>) => {
      state.isNextLoading = action.payload;
    },
    setIsSubmitLoading: (state, action: PayloadAction<boolean>) => {
      state.isSubmitLoading = action.payload;
    },
    setFailedLogin: (state, action: PayloadAction<number>) => {
      state.failedLogin = action.payload;
    },
    setSelectedValue: (state, action: PayloadAction<string>) => {
      state.selectedValue = action.payload;
    },
    setDialog: (state, action: PayloadAction<boolean>) => {
      state.dialog = action.payload;
    },
    setMagicLink: (state, action: PayloadAction<boolean>) => {
      state.magicLink = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setNoDefault: (state, action: PayloadAction<boolean>) => {
      state.noDefault = action.payload;
    },
    setIsSupported: (state, action: PayloadAction<boolean>) => {
      state.isSupported = action.payload;
    },
    setSdkReady: (state, action: PayloadAction<boolean>) => {
      state.sdkReady = action.payload;
    },
    setEmailList: (state, action: PayloadAction<any[]>) => {
      state.emailList = action.payload;
    },
    setTwoFACode: (state, action: PayloadAction<string>) => {
      state.twoFACode = action.payload;
    },
    setTwoFAError: (state, action: PayloadAction<string>) => {
      state.twoFAError = action.payload;
    },
    setIsTwoFALoading: (state, action: PayloadAction<boolean>) => {
      state.isTwoFALoading = action.payload;
    },
    setShowTwoFA: (state, action: PayloadAction<boolean>) => {
      state.showTwoFA = action.payload;
    },
    resetLoginForm: (state) => {
      state = initialState;
    },
  },
});

export const {
  setEmail,
  setEmailError,
  setPassword,
  setPasswordError,
  setShowPassword,
  setResetCode,
  setStep,
  setError,
  setIsNextLoading,
  setIsSubmitLoading,
  setFailedLogin,
  setSelectedValue,
  setDialog,
  setMagicLink,
  setLoading,
  setNoDefault,
  setIsSupported,
  setSdkReady,
  setEmailList,
  setTwoFACode,
  setTwoFAError,
  setIsTwoFALoading,
  setShowTwoFA,
  resetLoginForm,
} = LoginFormInputSlice.actions;

// Use useSelector directly to avoid circular dependency with store.ts
export const useLoginFormInputSlice = () =>
  useSelector((state: { loginFormInput: ILoginFormInputState }) => state.loginFormInput);

export { LoginFormInputSlice };
